<script setup lang="ts">
import { CtButton } from '@camelot/components'
import { storeToRefs } from 'pinia'
import { formatAmount, getOfferDaysDuration } from '~/composables/offer-utils'
import { translate, useTranslations } from '~/composables/translate/translations'
import { usePrebookingButtonLogic } from '~/composables/usePrebookingButtonLogic'
import { useExtrasStore } from '~/stores/extras'
import { useOfferStore } from '~/stores/offer'

const emits = defineEmits(['nextStep', 'detailsPressed'])

const { selectedOffer, getOfferDescription } = storeToRefs(useOfferStore())
const { totalExtraPrice } = storeToRefs(useExtrasStore())

const {
  isLoading,
  onButtonPress,
  buttonText,
  errorPrebookingCreation,
  isPaymentPage,
  paymentSetupStore,
} = usePrebookingButtonLogic()

const totalDuration = computed<string>(() => {
  const duration: number = getOfferDaysDuration()!
  return (duration === 1 ? '% day' : '% days')
    .replace('%', String(duration))
})

const totalPrice = computed<string | undefined>(() => formatAmount(
  useTranslations().language.value,
  (selectedOffer.value?.price ?? 0) + totalExtraPrice.value,
  useOfferStore().selectedOffer?.currency,
))
</script>

<template>
  <div class="bg-white z-50 md:hidden pl-0 pr-0 py-24">
    <div class="container mx-auto ">
      <div class="flex flex-col xs:flex-row md:items-center mx-24">
        <!-- Left column for text -->
        <div class="flex-1">
          <Transition mode="out-in" name="fade-bounce">
            <span :key="totalPrice">
              <span class="text-xl"><strong>{{ totalPrice }} </strong></span>
            </span>
          </Transition>
          <span class="ml-8 inline text-sm font-extralight text-black-80">{{ totalDuration }}</span>
          <p class="text-sm mt-2 font-extralight text-black-80">
            {{ useOfferStore().getFormattedPricePerDay }}/{{ translate('amount_per_day') }}
            <span class="text-sm text-black-80">
              {{ getOfferDescription }}
            </span>
          </p>
          <p class="text-rebel-100 font-medium" @click="emits('detailsPressed')">
            {{ translate('sidebar_price_booking_details') }}
          </p>
        </div>
        <!-- Right column for CtButton -->
        <div class="">
          <CtButton size="sm" :loading="isLoading" :disabled="isLoading || errorPrebookingCreation !== undefined || (isPaymentPage && !paymentSetupStore.isStripeReady)" @click="onButtonPress">
            {{ buttonText }}
          </CtButton>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.fade-bounce-leave-active {
  animation: bounce 0.3s, scale-opacity 0.3s;
}

.fade-bounce-enter-active {
  animation: bounce 0.1s, scale-opacity 0.1s;
}

.fade-bounce-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }

  40% {
    transform: translateY(-25px);
  }

  60% {
    transform: translateY(-10px);
  }
}

@keyframes scale-opacity {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }

  60% {
    opacity: 0.3;
    transform: scale(1.1);
  }
}
</style>
